import { useState, useEffect, useRef, RefObject } from 'react';
interface UseInViewOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  triggerOnce?: boolean;
}

interface UseInViewReturn {
  ref: RefObject<Element>;
  inView: boolean;
  entry: IntersectionObserverEntry | null;
}

export function useInView({
  root = null,
  rootMargin = '0px',
  threshold = 0,
  triggerOnce = false
}: UseInViewOptions = {}): UseInViewReturn {
  const [inView, setInView] = useState(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const ref = useRef<Element>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const enteredViewRef = useRef(false);

  useEffect(() => {
    // Skip if already triggered once and triggerOnce is true
    if (triggerOnce && enteredViewRef.current) {
      return;
    }

    // Skip if no element to observe
    if (!ref.current) {
      return;
    }

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        setEntry(entry);

        if (entry.isIntersecting) {
          enteredViewRef.current = true;

          // Disconnect observer if triggerOnce is true
          if (triggerOnce && observer) {
            observer.disconnect();
          }
        }
      },
      { root, rootMargin, threshold }
    );

    observer.observe(ref.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [root, rootMargin, threshold, triggerOnce]);

  return { ref, inView, entry };
}

export default useInView;